import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { MessageSquareWarning, ChevronUp, ShieldCheck, Plus } from "lucide-react";
import api from "../lib/api";
import { PageHeader, Spinner, Pill, fmtNum } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Complaints() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [cats, setCats] = useState([]);
  const [category, setCategory] = useState("");
  const [entityType, setEntityType] = useState("");
  const [sort, setSort] = useState("recent");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/complaints/categories").then((r) => setCats(r.data)); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/complaints", { params: { category, entity_type: entityType, sort, q, limit: 20 } });
      setItems(data.items); setTotal(data.total);
    } finally { setLoading(false); }
  }, [category, entityType, sort, q]);

  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q]);

  const upvote = async (e, c) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { toast.error("Sign in to upvote"); return; }
    try {
      const { data } = await api.post(`/complaints/${c.complaint_id}/upvote`);
      setItems((prev) => prev.map((x) => x.complaint_id === c.complaint_id ? { ...x, upvote_count: data.upvote_count, has_upvoted: data.has_upvoted } : x));
    } catch { toast.error("Could not upvote"); }
  };

  return (
    <div data-testid="complaints-page">
      <PageHeader eyebrow="Community" title="Complaints Forum"
        subtitle="Real traveller experiences with airlines and airports. Upvote what matters and track official responses.">
        <Link to="/complaints/new" data-testid="complaints-new-btn"
          className="inline-flex items-center gap-2 mt-5 bg-primary text-white px-5 py-2.5 text-sm font-semibold hover:bg-blue-700 transition-colors">
          <Plus className="w-4 h-4" /> File a complaint
        </Link>
      </PageHeader>

      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-wrap gap-3 mb-6">
          <input data-testid="complaints-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search complaints…"
            className="flex-1 min-w-[200px] border border-zinc-300 bg-white px-4 py-2.5 outline-none focus:ring-2 focus:ring-primary" />
          <select value={entityType} onChange={(e) => setEntityType(e.target.value)} data-testid="complaints-entity-filter"
            className="border border-zinc-300 bg-white px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary">
            <option value="">All targets</option>
            <option value="airline">Airlines</option>
            <option value="airport">Airports</option>
          </select>
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="complaints-category-filter"
            className="border border-zinc-300 bg-white px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary">
            <option value="">All categories</option>
            {cats.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="complaints-sort"
            className="border border-zinc-300 bg-white px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary">
            <option value="recent">Most recent</option>
            <option value="top">Most upvoted</option>
          </select>
        </div>

        {loading ? <Spinner /> : (
          <>
            <div className="text-sm text-muted-foreground mb-3 font-mono">{fmtNum(total)} published complaints</div>
            <div className="space-y-3 pb-10">
              {items.map((c) => (
                <Link key={c.complaint_id} to={`/complaints/${c.complaint_id}`} data-testid={`complaint-item-${c.complaint_id}`}
                  className="flex gap-4 border border-zinc-300 border-l-4 border-l-red-500 bg-white p-5 hover:-translate-y-px hover:border-primary transition-[transform,border-color]">
                  <button onClick={(e) => upvote(e, c)} data-testid={`upvote-${c.complaint_id}`}
                    className={`flex flex-col items-center justify-center w-14 shrink-0 border transition-colors ${c.has_upvoted ? "border-primary bg-blue-50 text-primary" : "border-zinc-300 text-zinc-600 hover:border-primary hover:text-primary"}`}>
                    <ChevronUp className="w-5 h-5" />
                    <span className="font-mono text-sm font-500">{c.upvote_count}</span>
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <Pill tone="red">{c.category}</Pill>
                      <Pill tone={c.entity_type === "airline" ? "blue" : "muted"}>{c.entity_name}</Pill>
                      {c.official_response && <Pill tone="green"><ShieldCheck className="w-3 h-3 mr-1 inline" />Official response</Pill>}
                      {c.status === "resolved" && <Pill tone="green">Resolved</Pill>}
                    </div>
                    <h3 className="font-heading font-700 text-lg leading-snug">{c.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{c.body}</p>
                    <div className="mt-2 text-xs font-mono text-muted-foreground">by {c.author_name} · {c.comment_count} comments · {new Date(c.created_at).toLocaleDateString()}</div>
                  </div>
                </Link>
              ))}
              {items.length === 0 && (
                <div className="py-20 text-center">
                  <MessageSquareWarning className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No complaints match your filters.</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
